import { redirect } from '@sveltejs/kit'
import equal from 'fast-deep-equal'
import { get } from 'svelte/store'

import { browser } from '$app/environment'
import { goto } from '$app/navigation'
import { cleanAccount, getAccountToPost } from '$lib/cleaners'
import environment from '$lib/environment'
import { getDayDiff } from '$lib/functions/helpers'
import { messages } from '$lib/messages'
import { apiPostJson, apiPostResponse } from '$lib/proxy/apiProxy'
import { account, ACCOUNT_TEST_INV_CY_ID } from '$lib/stores/account/account'
import { getTrialDays } from '$lib/stores/bo_settings'
import type { Account } from '$lib/types/client_types'
import { PlanKey } from '$lib/types/subscription_types'

import { capitalizeFirst } from '../../functions/formatters'
import { sentryMessage } from '../../functions/sentry'
import { debounced, SemaphoreKey, waitDebouncedFinish } from '../../semaphores'
import type { ChangeEmailParam, ChangeEmailRequestParam } from './types'

const appSubscriptionPlans: { plan_key: string; idx: number; name_visible: string }[] = [
	{ idx: 0, plan_key: 'basic', name_visible: 'Basic' },
	{ idx: 1, plan_key: 'pro', name_visible: 'Pro' },
	{ idx: 2, plan_key: 'advanced', name_visible: 'Advanced' }
]

export async function checkLoginSignup() {
	// MOVED to Singin and Signup forms
	// Problem New Invoice -> View PDF -> Signup -> SignIn -> PDF -> back
	if (['/login', '/signup'].includes(location.pathname)) {
		const acc = get(account)
		if (acc.registered) {
			console.log(`account:check already registered (on ${location.pathname})`)

			//Check it document.referrer is from my own site
			if (document.referrer.indexOf(location.protocol + '//' + location.host) === 0)
				history.back() // this is by view PDF
			else goto('/')
		}
		return null
	}
	if (['/login', '/signup'].includes(location.pathname)) {
		return
	}
}


export async function signIn(email: string, password: string, id: string='') {
	const url = `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${environment.googleApiKey}`

	const response = await fetch(url, {
		method: 'POST',
		body: JSON.stringify({
			email,
			password,
			returnSecureToken: true
		})
	})
	const jsonFirebase = await response.json()

	if (jsonFirebase.error) {
		const error = new Error(jsonFirebase.error.message)
		throw error
	}

	// const jsonData = await apiPostJson('/account/login', jsonFirebase)
	jsonFirebase.invitation_id = id
	const jsonData = await apiPostJson('/user/login', jsonFirebase)

	if (jsonData?.error) {
		// const error = new Error(jsonData.error.message)
		// throw error
		throw jsonData?.error
	}


	account.set({
		...jsonData.account,
		active: true
	})

	const currentUrl = new URL(location.href)
	//	const nextUrl = currentUrl.searchParams.get('next') || '/invoices';
	const nextUrl = currentUrl.searchParams.get('next') || '/'
	return nextUrl
}

export async function signUp(email: string, password: string, id: string='') {
	// const endpoint = 'signUp'
	const url = `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${environment.googleApiKey}`

	const response = await fetch(url, {
		method: 'POST',
		body: JSON.stringify({
			email,
			password,
			returnSecureToken: true
		})
	})
	const jsonFirebase = await response.json()


	if (jsonFirebase.error) {
		// the if is because returns: "WEAK_PASSWORD : Password should be at least 6 characters"
		if (jsonFirebase.error.message.startsWith('WEAK_PASSWORD')) {
			throw new Error('WEAK_PASSWORD')
		}
		const error = new Error(jsonFirebase.error.message)
		throw error
	}

	// const jsonData = await apiPostJson('/account/login', jsonFirebase)
	// const jsonData = await apiPostJson('/user/signup', jsonFirebase)
	jsonFirebase.invitation_id = id
	const jsonData = await apiPostJson('/user/login', jsonFirebase)

	if (jsonData?.error) {
		// const error = new Error(jsonData.error.message)
		// throw error
		throw jsonData?.error
	}

	account.set({
		...jsonData.account,
		active: true
	})

	/* -------------------------------------------------- */
	/* DON'T REMOVE - used by GTM (Microsoft Advertising) */
	/* -------------------------------------------------- */
	const currentUrl = new URL(location.href)
	console.log('account.ts - next: ', currentUrl.searchParams.get('next'))
	const nextUrl = currentUrl.searchParams.get('next') || '/' 			// '/invoices/new'
	if (jsonData.new) {
		return nextUrl + (nextUrl.includes('?') ? '&' : '?') + 'newAccount=true&subscribe'
	}

	console.log('account.ts - nextUrl: ', nextUrl)
	return nextUrl
}

export async function sendOobCode(email: string) {
	/* DOC: https://cloud.google.com/identity-platform/docs/use-rest-api?hl=en#section-send-password-reset-email
        curl 'https://identitytoolkit.googleapis.com/v1/accounts:sendOobCode?key=[API_KEY]' \
        -H 'Content-Type: application/json' \
        --data-binary '{"requestType":"PASSWORD_RESET","email":"[user@example.com]"}'
    */
	const url = `https://identitytoolkit.googleapis.com/v1/accounts:sendOobCode?key=${environment.googleApiKey}`
	const response = await fetch(url, {
		method: 'POST',
		body: JSON.stringify({
			requestType: 'PASSWORD_RESET',
			email: email
		})
	})
	const jsonData = await response.json()
	if (jsonData?.error) {
		const error = new Error(jsonData.error.message)
		throw error
	}

	return
}

export enum OAuthProvider {
	Google = 'google.com'
}

export async function signInWithProvider(provider: OAuthProvider) {
	const currentUrl = new URL(location.href)
	const next = currentUrl.searchParams.get('next')
	//const invoicerActId = currentUrl.searchParams.get('invoicerActId');

	// const nextUrl = new URL(environment.apiUrl + '/account/provider/login')
	const nextUrl = new URL(environment.apiUrl + '/user/provider/login')

	nextUrl.searchParams.set('provider', provider)
	if (next) {
		nextUrl.searchParams.set('next', next)
	}

	// Cookies.set(CHECK_COOKIE_NAME, 'true', { expires: 365 * 10 });
	console.log('account.ts - signInWithProvider - nextUrl: ', nextUrl.toString())
	//alert("account.ts - signInWithProvider - nextUrl: " + nextUrl.toString())
	//debugger;

	location.href = nextUrl.toString()
}

// Updates store immediately, then schedules a save
export function setAccount(updates: object, skipSave: boolean=false) {
	const oldAccount = get(account)
	account.update((existing) => ({
		...existing,
		...updates
	}))
	const newAccount = get(account)
	if (equal(cleanAccount(oldAccount), cleanAccount(newAccount))) {
		return
	}
	if (!skipSave) {
		debounced(SemaphoreKey.Account, saveAccount)
	}
}

export async function setAccountLogo(logo: string) {
	console.log('account:setLogo')
	const data = { logo: logo }
	const jsonData = await apiPostJson('/account/update_logo', data)
	if (jsonData?.error) {
		messages.error(jsonData?.error.message)
	} else {
		account.update((existing) => ({
			...existing,
			logo: logo
		}))
	}
}

export async function saveAccount() {
	console.log('account-save')

	// avoid sending property logo
	const dataFull = getAccountToPost(cleanAccount(get(account)))
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { logo, ...data } = dataFull

	const jsonData = await apiPostJson('/account/update', data)
	if (jsonData?.error) {
		messages.error('Could not update your account - logout and login, or contact support')
	}
}

export async function logout() {
	await waitDebouncedFinish(SemaphoreKey.Total)
	await apiPostResponse('/user/logout')

	location.href = '/login'
}

export function trialIsOver(): boolean {
	const trial_days = getTrialDays()
	const dayDiff = getDayDiff(Date.now(), Date.parse(get(account).registered_at))
	const trialIsOver = dayDiff > trial_days
	return trialIsOver
}

export function trialDaysRemaining(): number {
	const trial_days = getTrialDays()
	const dayDiff = getDayDiff(Date.now(), Date.parse(get(account).registered_at))
	const daysRemaining = trial_days - dayDiff
	return daysRemaining
}

export function isSubscriptionNeeded() {
	const act = get(account)
	const result =
		trialIsOver() &&
		act.stripe_subscription_status !== 'active' &&
		act.stripe_subscription_status !== 'past_due'

	return result
}

export function isAccountOfPlanAdvanced(): boolean {
	const act = get(account)
	const result = act.subscription_plan_key === PlanKey.Advanced
	return result
}

export function getAccountPlanName(plan: string) {
	const subsPlan = appSubscriptionPlans.find((planKey) => planKey.plan_key === plan)
	if (!subsPlan) {
		return plan.replace(/\w\S*/g, function (txt: string) {
			return capitalizeFirst(txt)
		})
	}
	return subsPlan.name_visible
}

export function compareAccountPlan(plan1: string, plan2: string): number {
	const subsPlan1 = appSubscriptionPlans.find((planKey) => planKey.plan_key === plan1)
	const subsPlan2 = appSubscriptionPlans.find((planKey) => planKey.plan_key === plan2)
	if (!subsPlan1 || !subsPlan2) {
		return 2
	}

	if (subsPlan1.idx > subsPlan2.idx) return 1
	if (subsPlan1.idx < subsPlan2.idx) return -1
	return 0
}

export function checkDocVsSessionAccId(docAccId: string, sessionAcc: Account, next?: string) {
	if (docAccId !== sessionAcc.id) {
		const warnMsg = `pre-loading doc.account_id:${docAccId} != session.account_id:${sessionAcc.id}`

		if (docAccId != ACCOUNT_TEST_INV_CY_ID) {
			sentryMessage(warnMsg)
		}

		if (sessionAcc.registered) {
			throw redirect(302, '/')
		} else {
			let target = '/login'
			if (next && next != '') target += `?next=${next}`
			throw redirect(302, target)
		}
	}
}

export async function saveAccountReferrer(qss: string) {

	if (!browser) return

	// Decode the encoded query string
	const decodedQueryString = decodeURIComponent(qss)

	// Parse the decoded query string
	const queryParams = new URLSearchParams(decodedQueryString)
	const referrer_page = queryParams.get('referrer_page')
	const landing_page = queryParams.get('landing_page')

	if (referrer_page || landing_page){
		const params = {referrer_page, landing_page, qss}
		apiPostJson('/account_referrers/insert', params)
	}
}

export async function verifyNewEmailAndSendAuthCode(params: ChangeEmailRequestParam) {

	const jsonData = await apiPostJson('/account/emailchange/chknewemailandsendcode', params)
	
	if (jsonData?.error) {
		const error = jsonData.error.message
		messages.error(`Error sending auth code: ${error}`)
		return { sent: false, error: error }
	}

	return { sent: true, error: '' }
}

export async function sendAuthCode(params: ChangeEmailRequestParam) {
	const jsonData = await apiPostJson('/account/emailchange/sendauthcode', params)
	if (jsonData?.error) {
		const error = jsonData.error.message
		messages.error(`Error sending auth code: ${error}`)
		return false
	}

	return true
}


export async function changeEmail(params: ChangeEmailParam) {

	const jsonData = await apiPostJson('/account/emailchange/update', params)
	return jsonData
}
